<script setup>
import { onBeforeMount, onBeforeUnmount, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useCategories } from "../../Composables/useCategories.js"
import _ from 'lodash'
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()

const storageKey = "Shiftech_TicketsCategoryList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: categories,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
} = useDataManager({
    baseUrl: '/api/categories?category_type=App\\Models\\Ticket',
    baseSearchUrl: '/api/search/categories?category_type=ticket',
    defaultSortingField: 'created_at',
    defaultSortingFieldsOrder: {
        id: 'asc',
        name: 'asc',
        created_at: 'asc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: '#ID', field: 'id', sortable: true, slot: 'id', class: 'col-1' },
    { label: 'Nom', field: 'name', sortable: true, slot: 'name', class: 'col-4' },
    { label: 'Utilisateur par défaut', slot: 'default_user', class: 'col-3' },
    { label: 'Tickets', slot: 'count', class: 'col-1 text-center' },
    { label: 'Date création', field: 'created_at', sortable: true, slot: 'created_at', class: 'col-2' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
    }

    loadData()
})

watch(
    [sortingField, sortingFieldsOrder, query, searchLoaded],
    ([newSortingField, newSortingOrder, newQuery, newSearchLoaded]) => {
        saveToLocalStorage({
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
        })
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

const idToDelete = ref(null)

function showModal(modalId, id) {
    idToDelete.value = id
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const {
    deleteCategory,
} = useCategories({
    onDeleted: () => {
        loadData(url.value)
        hideModal('modalDelete')
    },
    onError: (error) => httpErrorsHandler(error),
})
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card" id="contactList">
                <div class="card-header">
                    <div class="row g-3">
                        <div class="col-md-4">
                            <div class="search-box search-box-list">
                                <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchTicketCategories">
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>
                        <div class="col-md-auto ms-auto">
                            <router-link :to="{ name:'tickets-categories.create' }" class="btn btn-secondary">
                                <i class="ri-add-fill me-1 align-bottom"></i> Ajouter
                            </router-link>
                        </div>
                    </div>
                </div>

                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="categories"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- ID -->
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">#{{ item.data.id }}</span>
                        </template>

                        <!-- Name -->
                        <template #name="{ item }">
                            {{ item.data.name }}
                        </template>

                        <!-- Default User -->
                        <template #default_user="{ item }">
                            <span v-if="item.data.default_user?.data">
                                {{ item.data.default_user.data.first_name }} {{ item.data.default_user.data.last_name }}
                            </span>
                            <span class="text-muted" v-else>-</span>
                        </template>

                        <!-- Count -->
                        <template #count="{ item }">
                            <span class="badge fs-11 rounded-pill bg-danger-subtle text-danger" v-if="item.data.ticket_count === 0">{{ item.data.ticket_count }}</span>
                            <span class="badge fs-11 rounded-pill bg-secondary-subtle text-secondary" v-else>{{ item.data.ticket_count }}</span>
                        </template>

                        <!-- Created at -->
                        <template #created_at="{ item }">
                            {{ item.data.created_at_fr }}
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link :to="{ name: 'tickets-categories.edit', params: { id: item.data.id } }" class="text-info" title="Modifier la cat\u00e9gorie" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- DELETE -->
                            <a href="#" class="text-danger" title="Supprimer la cat\u00e9gorie" @click.prevent="showModal('modalDelete', item.data.id)" v-if="item.can.delete">
                                <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                            </a>
                            <i class="ri-delete-bin-3-fill fs-17 px-1 text-muted" v-else></i>
                        </template>
                    </data-table>

                    <modal id="modalDelete" title="Supprimer la cat\u00e9gorie ?" btn-class="btn-danger" btn-text="Supprimer" @click:button="deleteCategory(idToDelete)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous \u00eates sur le point de supprimer une cat\u00e9gorie de ticket.</p>
                        </template>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
